/**
 * Base Screen Component
 * Provides common layout and functionality for all verification screens
 * Now supports display modes (standalone vs embedded)
 */

import { type ReactNode, useEffect, useState } from 'react';
import { useDisplayModeContext } from '../../hooks/useDisplayModeContext';
import { designTokens } from '../../constants/designTokens';
import { Footer } from './Footer';
import { runtimeConfig } from '../../services/RuntimeConfigService';
import { sessionStateManager } from '../../utils/SessionStateManager';

interface BaseScreenProps {
  children: ReactNode;
  className?: string;
  showProgress?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

export function BaseScreen({ children, className = '' }: BaseScreenProps) {
  // Get display mode configuration
  const { mode } = useDisplayModeContext();
  const [sandboxMode, setSandboxMode] = useState(false);

  useEffect(() => {
    const sessionSandbox = sessionStateManager.getState()?.config?.sandboxMode;
    if (typeof sessionSandbox === 'boolean') {
      setSandboxMode(sessionSandbox);
      return;
    }

    runtimeConfig
      .loadConfig()
      .then((config) => {
        setSandboxMode(Boolean(config.features?.sandboxMode));
      })
      .catch(() => {
        // Ignore missing runtime config to avoid blocking UI
      });
  }, []);

  return (
    <div className={`screen-container ${className}`} data-display-mode={mode}>
      {sandboxMode && (
        <div className="sandbox-banner" role="status" aria-live="polite">
          <span className="sandbox-label">Sandbox</span>
          <span className="sandbox-text">Verification runs in sandbox mode</span>
        </div>
      )}
      {/* Main content */}
      <main className="screen-content">
        <div className="screen-body">{children}</div>
        <Footer />
      </main>

      <style>{`
        /* Base container styles */
        .screen-container {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          /* Use safe viewport height on Safari */
          min-height: 100dvh; /* Dynamic viewport height */
          background-color: var(--verify-screen-bg);
          color: ${designTokens.colors.text.primary};
          font-family: ${designTokens.typography.fontFamily.primary};
          
          /* Clean, flat design - remove any potential container effects */
          border: none;
          box-shadow: none;
          outline: none;
        }

        .sandbox-banner {
          align-self: center;
          display: inline-flex;
          align-items: center;
          gap: ${designTokens.spacing.xs};
          margin-top: ${designTokens.spacing.sm};
          padding: 6px ${designTokens.spacing.md};
          border-radius: ${designTokens.borderRadius.full};
          background: var(--verify-neutral-white, #ffffff);
          border: 1px solid var(--brand-primary, #2563eb);
          color: var(--brand-primary, #2563eb);
          font-size: ${designTokens.typography.fontSize.xs};
          font-weight: ${designTokens.typography.fontWeight.semibold};
          letter-spacing: 0.03em;
          text-transform: uppercase;
        }

        .sandbox-text {
          text-transform: none;
          letter-spacing: 0;
          color: var(--verify-text-muted, #6B7280);
          font-weight: ${designTokens.typography.fontWeight.normal};
        }

        /* Standalone mode - Full viewport usage */
        .screen-container[data-display-mode="standalone"] {
          min-height: 100vh;
          min-height: 100dvh; /* Dynamic viewport height for Safari */
          width: 100%;
          position: relative;
          overflow-x: hidden;
          background-color: var(--verify-screen-bg);
        }

        /* Embedded mode - Flexible container */
        .screen-container[data-display-mode="embedded"] {
          min-height: 100vh;
          min-height: 100dvh; /* Dynamic viewport height for Safari */
          width: 100%;
          position: relative;
          top: auto;
          left: auto;
          overflow: visible;
        }

        /* Content styles for standalone mode */
        .screen-container[data-display-mode="standalone"] .screen-content {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding: ${designTokens.spacing.md} ${designTokens.spacing.lg};
          padding-bottom: calc(${designTokens.spacing.lg} + env(safe-area-inset-bottom));
          overflow-x: hidden;
          overflow-y: visible;
        }

        .screen-container[data-display-mode="standalone"] .screen-body {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex: 1 0 auto;
        }

        /* Content styles for embedded mode */
        .screen-container[data-display-mode="embedded"] .screen-content {
          flex: 1;
          padding: 0 20px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding-top: 60px; /* Consistent top anchor positioning */
        }

        .screen-container[data-display-mode="embedded"] .screen-body {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        /* Mobile-First Responsive Design System */
        
        /* Base mobile styles (320px+) */
        
        .screen-container {
          touch-action: manipulation; /* Optimizes touch performance */
          -webkit-tap-highlight-color: transparent; /* Remove iOS tap highlight */
          user-select: none; /* Prevent text selection on touch devices */
        }
        
        .screen-header {
          padding: 8px 12px;
          min-height: 44px; /* iOS minimum touch target */
          position: sticky;
          top: 0;
          background-color: var(--verify-header-bg);
          backdrop-filter: blur(10px);
          z-index: 10;
        }
        
        .back-button {
          min-width: 44px; /* iOS minimum touch target */
          min-height: 44px;
          padding: 8px 12px;
          font-size: 14px;
          border-radius: 8px;
          -webkit-tap-highlight-color: transparent;
        }
        
        .progress-container {
          flex-shrink: 0;
        }
        
        .progress-bar {
          width: 60px;
          height: 3px;
        }
        
        .progress-text {
          font-size: 11px;
          min-width: 40px;
        }
        
        .screen-title {
          font-size: 18px;
          margin: 16px 12px 12px;
          padding: 0 4px;
          line-height: 1.2;
        }
        
        /* Mobile adjustments are handled by media queries below */
        
        /* Small mobile adjustments (320px-375px) */
        @media (max-width: 375px) {
          .sandbox-banner {
            margin-top: ${designTokens.spacing.xs};
            padding: 3px ${designTokens.spacing.sm};
          }

          .sandbox-text {
            display: none;
          }
          .screen-header {
            padding: 6px 8px;
          }
          
          .back-button {
            padding: 6px 8px;
            font-size: 13px;
          }
          
          .progress-bar {
            width: 50px;
          }
          
          .progress-text {
            font-size: 10px;
          }
          
          .screen-title {
            font-size: 16px;
            margin: 12px 8px 8px;
          }
          
          .screen-container[data-display-mode="embedded"] .screen-content {
            padding: 0 8px 8px;
            padding-top: 60px;
          }
        }
        
        @media (max-width: 600px), (max-height: 600px) {
          .screen-container[data-display-mode="standalone"] .screen-body {
            justify-content: flex-start;
          }
        }

        @media (min-width: 600px) and (min-height: 600px) {
          .screen-container[data-display-mode="standalone"] {
            height: 100vh;
            height: 100dvh; /* Constrain scroll container on larger devices */
            width: 100vw;
            position: fixed;
            top: 0;
            left: 0;
            overflow: hidden;
          }

          .screen-container[data-display-mode="standalone"] .screen-content {
            overflow-y: auto;
            /* Ensure content is scrollable on mobile Safari */
            -webkit-overflow-scrolling: touch;
          }
        }
        
        /* Mobile landscape (568px+) */
        @media (min-width: 568px) and (max-height: 400px) {
          .screen-container {
            min-height: 100vh;
          }
          
          .screen-header {
            padding: 6px 16px;
            min-height: 40px;
          }
          
          .screen-title {
            font-size: 16px;
            margin: 8px 16px 6px;
          }
          
          .screen-container[data-display-mode="embedded"] .screen-content {
            padding: 0 16px 8px;
            padding-top: 60px;
          }
        }
        
        /* Large mobile/small tablet (480px+) */
        @media (min-width: 480px) {
          .screen-header {
            padding: 12px 20px;
          }
          
          .back-button {
            font-size: 15px;
            padding: 8px 12px;
          }
          
          .progress-bar {
            width: 80px;
          }
          
          .progress-text {
            font-size: 12px;
          }
          
          .screen-title {
            font-size: 20px;
            margin: 20px 20px 16px;
          }
          
          .screen-container[data-display-mode="embedded"] .screen-content {
            padding: 0 20px 12px;
            padding-top: 60px; /* Maintain mobile top anchor */
          }
        }
        
        /* Tablet portrait (600px+) */
        @media (min-width: 600px) {
          .screen-header {
            padding: 16px 24px;
          }
          
          .back-button {
            font-size: 16px;
          }
          
          .progress-bar {
            width: 100px;
          }
          
          .screen-title {
            font-size: 22px;
            margin: 24px 24px 20px;
          }
          
          .screen-container[data-display-mode="embedded"] .screen-content {
            padding: 0 24px 16px;
            padding-top: 80px; /* Tablet top anchor */
          }
        }
        
        /* Desktop/large tablet (840px+) - Clean, flat design */
        @media (min-width: 840px) {
          .screen-container[data-display-mode="embedded"] {
            /* Container constraints only for embedded mode */
            max-width: 840px;
            margin: 0 auto;
            /* No border-radius, box-shadow, or overflow for clean flat appearance */
          }
          
          .screen-header {
            padding: 16px 32px;
            position: static;
            background-color: var(--verify-header-bg-solid);
            backdrop-filter: none;
          }
          
          .progress-bar {
            width: 120px;
          }
          
          .screen-title {
            font-size: 24px;
            margin: 32px 32px 24px;
          }
          
          .screen-container[data-display-mode="embedded"] .screen-content {
            padding: 0 32px 20px;
            padding-top: 100px; /* Desktop top anchor */
          }
        }
        
        /* High-DPI displays optimization */
        @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
          .progress-bar {
            border: 0.5px solid rgba(255, 255, 255, 0.1);
          }
        }
        
        /* Reduced motion accessibility */
        @media (prefers-reduced-motion: reduce) {
          .progress-fill {
            transition: none;
          }
          
          .back-button {
            transition: none;
          }
        }
        
        /* Dark mode optimization */
        @media (prefers-color-scheme: dark) {
          .screen-container {
            background-color: var(--verify-screen-bg);
          }

          .screen-header {
            background-color: var(--verify-header-bg-dark);
            border-bottom-color: var(--verify-border-muted);
          }

          .sandbox-banner {
            background: var(--verify-bg-secondary, #374151);
          }

          .sandbox-text {
            color: var(--verify-text-secondary, #9CA3AF);
          }
        }
        
        /* Focus management for keyboard navigation */
        .back-button:focus-visible {
          outline: 2px solid var(--brand-primary);
          outline-offset: 2px;
        }
        
        /* Global responsive image constraints */
        .screen-content img {
          max-height: 45vh; /* Prevent images from dominating viewport */
        }
        
        /* More conservative on tablets where we have more width */
        ${designTokens.media.tablet} {
          .screen-content img {
            max-height: 40vh;
          }
        }

        /* More conservative on desktop where we have more width */
        ${designTokens.media.desktop} {
          .screen-content img {
            max-height: 40vh;
          }
        }
      `}</style>
    </div>
  );
}
